import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useLang } from '@/context/LangContext';
import { Paperclip, X, Search, Dribbble } from 'lucide-react';

/**
 * Render a small "attached play" preview card inside a message bubble.
 * Looks like: [court icon] Play Name · category → View
 */
export function PlayAttachmentBadge({ summary }) {
  const { t } = useLang();
  if (!summary?.id) return null;
  return (
    <Link
      to={`/playbook/${summary.id}`}
      data-testid={`msg-play-attachment-${summary.id}`}
      className="mt-2 inline-flex items-center gap-2 bg-navy-deep/80 border border-gold/40 rounded-md px-2.5 py-1.5 hover:border-gold transition max-w-full"
    >
      <Dribbble className="w-3.5 h-3.5 text-gold flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-h uppercase tracking-wider text-gold truncate">{summary.name}</div>
        {summary.category && (
          <div className="text-[10px] text-gray-400 font-mono uppercase tracking-widest">{summary.category}</div>
        )}
      </div>
      <span className="text-[10px] text-gold/80 ml-1 flex-shrink-0">{t('view') || 'Ver'} →</span>
    </Link>
  );
}

/**
 * Compact button + popover to attach a play to a message before sending.
 * Calls `onSelect(playObjectOrNull)` when the user picks or clears.
 */
export function AttachPlayPicker({ selected, onSelect }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [plays, setPlays] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || plays.length > 0) return;
    setLoading(true);
    api.get('/plays')
      .then(r => setPlays(r.data.plays || []))
      .finally(() => setLoading(false));
  }, [open, plays.length]);

  const filtered = plays.filter(p =>
    !filter || p.name.toLowerCase().includes(filter.toLowerCase())
  ).slice(0, 30);

  return (
    <div className="relative">
      {selected ? (
        <div className="flex items-center gap-1.5 bg-gold/10 border border-gold/40 rounded-md px-2 py-1 text-xs">
          <Dribbble className="w-3 h-3 text-gold" />
          <span className="text-gold max-w-[140px] truncate">{selected.name}</span>
          <button
            type="button"
            onClick={() => onSelect(null)}
            data-testid="msg-attach-clear"
            className="text-gold/70 hover:text-alert"
            aria-label="Remove attachment"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          data-testid="msg-attach-toggle"
          title={t('msg_attach_play') || 'Adjuntar jugada'}
          className="p-2 rounded text-gold border border-gold/30 hover:bg-gold/10"
        >
          <Paperclip className="w-4 h-4" />
        </button>
      )}

      {open && !selected && (
        <div className="absolute bottom-12 left-0 z-30 bg-card-steel border-2 border-gold/40 rounded-lg shadow-2xl w-72 p-3" data-testid="msg-attach-popover">
          <div className="flex items-center gap-2 mb-2">
            <Search className="w-3 h-3 text-gold" />
            <input
              autoFocus
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder={t('search') || 'Buscar'}
              className="flex-1 bg-navy-deep border border-steel rounded px-2 py-1 text-xs text-white"
            />
            <button type="button" onClick={() => setOpen(false)} className="text-gold/70 hover:text-alert">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="max-h-60 overflow-y-auto divide-y divide-gold/10">
            {loading && <div className="text-xs text-gray-400 py-4 text-center">{t('loading')}…</div>}
            {!loading && filtered.length === 0 && (
              <div className="text-xs text-gray-400 py-4 text-center">{t('assign_no_plays') || 'No hay jugadas'}</div>
            )}
            {filtered.map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => { onSelect({ id: p.id, name: p.name, category: p.category }); setOpen(false); setFilter(''); }}
                data-testid={`msg-attach-pick-${p.id}`}
                className="w-full text-left px-2 py-2 hover:bg-gold/10 rounded text-xs"
              >
                <div className="font-h uppercase tracking-wider text-white truncate">{p.name}</div>
                <div className="text-[10px] text-gold/70 uppercase tracking-widest">{p.category} · {p.difficulty}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
